// Demo-only mockup of the AI-generated incident PDF report. Rendered inside
// the outage-simulation fan-out flow so viewers can see what the "Generate
// AI Report" step would actually produce, without a real PDF pipeline call.
// Visually mirrors the real report's fonts (Public Sans / JetBrains Mono —
// the app's committed Inter/JetBrains Mono pairing) and layout: a single
// portrait "page" with caption/value sections, so it reads as a preview of
// an actual document rather than a generic in-app card. Fully local/
// decorative: no network call, no real PDF is generated.
import '../styles/ai-report-mockup.css';

export interface AIReportMockupIncident {
  id: string | number;
  severity: string;
  current_status: string;
  impact_statement: string | null;
  service_name: string | null;
  affected_site: string | null;
  estimated_users_impacted: number | null;
  workaround: string | null;
  is_demo: true;
  state: string;
  declared_at: string;
}

function formatDeclaredAt(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function reportRef(id: string | number): string {
  const s = String(id).replace(/-/g, '').toUpperCase();
  return `AIR-${s.slice(0, 8) || '00000000'}`;
}

// A best-effort "how it broke" line, pattern-matched from the incident's own
// impact statement rather than invented from nothing — keeps the same
// honesty posture as the real report's LLM prompt (reports/collect/
// incident.js: "never invent a detail not present in the facts"), just
// applied to a demo mockup instead of a real model call. Explicitly framed
// as preliminary/unconfirmed in the section label below, same as a real
// incident commander would treat an automated correlation.
function probableCause(incident: AIReportMockupIncident): string {
  const text = (incident.impact_statement || '').toLowerCase();
  if (text.includes('timeout') || text.includes('timing out')) {
    return 'Elevated request latency consistent with a downstream dependency timeout or connection-pool exhaustion.';
  }
  if (text.includes('replication') || text.includes('lag')) {
    return 'Replica lag consistent with a slow query, index rebuild, or network partition between primary and replica.';
  }
  if (text.includes('login') || text.includes('auth') || text.includes('rejected')) {
    return 'Authentication failures consistent with an expired credential, misconfigured identity provider, or a recent auth-service deploy.';
  }
  if (text.includes('link down') || text.includes('network')) {
    return 'Loss of network reachability consistent with a circuit failure or routing change at the affected site.';
  }
  if (text.includes('queue') || text.includes('delayed')) {
    return 'Growing queue depth consistent with a downstream consumer outage or a throughput regression from a recent change.';
  }
  return 'Correlated with recent deploys and configuration changes to the affected service; no single root cause isolated yet.';
}

const NEXT_STEPS = [
  'Contain — apply the workaround above to restore service for affected users.',
  'Diagnose — review recent deploys, config changes, and alert history for the affected service.',
  'Remediate — roll back or patch the identified change once confirmed.',
  'Verify — confirm error rate and latency return to baseline before closing.',
];

export default function AIReportMockup({
  incident,
  onClose,
}: {
  incident: AIReportMockupIncident;
  onClose: () => void;
}) {
  const usersLine =
    typeof incident.estimated_users_impacted === 'number'
      ? incident.estimated_users_impacted.toLocaleString()
      : null;

  return (
    <div className="ai-report-mock-overlay" onClick={onClose}>
      <div className="ai-report-mock-shell" onClick={(e) => e.stopPropagation()}>
        <div className="ai-report-mock-toolbar">
          <span className="ai-report-mock-toolbar-label">Report Preview</span>
          <button
            type="button"
            className="ai-report-mock-close"
            onClick={onClose}
            aria-label="Close preview"
          >
            ✕
          </button>
        </div>

        <div className="ai-report-mock-page">
          <div className="ai-report-mock-page-head">
            <div className="ai-report-mock-page-head-text">
              <div className="ai-report-mock-eyebrow">
                {incident.service_name || 'Major Incident'}
              </div>
              <h1 className="ai-report-mock-title">Incident Report</h1>
            </div>
            <span className="ai-report-mock-draft-chip">DRAFT</span>
          </div>

          <div className="ai-report-mock-meta">
            <span>{reportRef(incident.id)}</span>
            <span className="ai-report-mock-meta-dot">·</span>
            <span>Declared {formatDeclaredAt(incident.declared_at)}</span>
            <span className="ai-report-mock-meta-dot">·</span>
            <span className="ai-report-mock-meta-sev">{incident.severity}</span>
          </div>

          <div className="ai-report-mock-divider" />

          <div className="ai-report-mock-section">
            <div className="ai-report-mock-caption">Impact</div>
            <p className="ai-report-mock-value">
              {incident.impact_statement || 'Impact statement not yet available.'}
            </p>
          </div>

          {(incident.affected_site || usersLine) && (
            <div className="ai-report-mock-section ai-report-mock-section--row">
              {incident.affected_site && (
                <div className="ai-report-mock-subsection">
                  <div className="ai-report-mock-caption">Affected Site</div>
                  <p className="ai-report-mock-value">{incident.affected_site}</p>
                </div>
              )}
              {usersLine && (
                <div className="ai-report-mock-subsection">
                  <div className="ai-report-mock-caption">Est. Users Impacted</div>
                  <p className="ai-report-mock-value">{usersLine}</p>
                </div>
              )}
            </div>
          )}

          {incident.workaround && (
            <div className="ai-report-mock-section">
              <div className="ai-report-mock-caption">Workaround</div>
              <p className="ai-report-mock-value">{incident.workaround}</p>
            </div>
          )}

          <div className="ai-report-mock-section">
            <div className="ai-report-mock-caption">Probable Cause (AI-correlated, unconfirmed)</div>
            <p className="ai-report-mock-value">{probableCause(incident)}</p>
          </div>

          <div className="ai-report-mock-section">
            <div className="ai-report-mock-caption">Next Steps</div>
            <ol className="ai-report-mock-steps">
              {NEXT_STEPS.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </div>

          <div className="ai-report-mock-section">
            <div className="ai-report-mock-caption">Estimated Resolution</div>
            <p className="ai-report-mock-value ai-report-mock-value--muted">
              Not yet estimated
            </p>
          </div>

          <div className="ai-report-mock-spacer" />

          <div className="ai-report-mock-ai-tag">✦ Generated by Stella AI</div>

          <div className="ai-report-mock-footer">
            <span>{reportRef(incident.id)}</span>
            <span>Page 1 of 1</span>
          </div>
        </div>

        <div className="ai-report-mock-actionbar">
          <span className="ai-report-mock-actionbar-note">
            Preview only — this demo does not generate a real PDF.
          </span>
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
